// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import {
  rowValidationErrorSchema,
  validationErrorFieldSchema,
} from '@/csv-import/schemas';
import { z } from 'zod';

const processedTimeUpdateItemSchema = z.object({
  index: z.number(),
  key: z.string(),
  updated: z.boolean(),
  noOp: z.boolean(),
  fieldsUpdated: z
    .record(z.boolean())
    .nullable()
    .transform((value) => value ?? {}),
  errors: z
    .array(validationErrorFieldSchema)
    .nullable()
    .transform((value) => value ?? []),
});

const timeDataUpdateResultSchema = z.object({
  success: z.boolean(),
  items: z.array(processedTimeUpdateItemSchema),
  structureErrors: z.array(validationErrorFieldSchema),
  rowErrors: z.array(rowValidationErrorSchema),
});

const uploadTimeDataUpdateResponseSchema = z.object({
  data: timeDataUpdateResultSchema,
  metadata: responseMetadataSchema,
});

type TimeDataUpdateResult = z.infer<typeof timeDataUpdateResultSchema>;

export function parseTimeDataUpdateErrorResponse(
  data: unknown
): TimeDataUpdateResult | null {
  const parsed = uploadTimeDataUpdateResponseSchema.safeParse(data);
  return parsed.success ? parsed.data.data : null;
}

export async function uploadTimeDataUpdate(
  formData: FormData
): Promise<TimeDataUpdateResult> {
  const url = '/v1/import/time-data-update';
  const response = await api.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  const parsed = await parseResponse(
    uploadTimeDataUpdateResponseSchema,
    response.data,
    {
      schemaName: 'UploadTimeDataUpdate',
      endpoint: url,
    }
  );
  return parsed.data;
}
